import { cn } from "@lib/cn";
import { IconUsersPlus } from "@tabler/icons-react";
import { go as fuzzysort } from "fuzzysort";
import { type KeyboardEvent, RefObject, useMemo, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import Input from "@/components/ui/input";
import { useGetCurrentUserQuery, useGetUsersQuery } from "@/shared/services";
import type { IStoriesFolderUser } from "@/shared/types";
import type { IUser } from "@/types";

interface IProps {
  onSelect: (user: IUser) => void;
  isShareEveryone: boolean;
  selectedUsers: IStoriesFolderUser[];
}

export const MembersDialogInput = ({
                                     isShareEveryone,
                                     onSelect,
                                     selectedUsers,
                                   }: IProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState(-1);

  const { data: currentUser } = useGetCurrentUserQuery();
  const containerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerRef as RefObject<HTMLElement>, () => {
    setSelectedSearch(-1);
    setIsOpen(false);
  });

  const { data: allUsers = [], isFetching: isLoadingUsers } =
    useGetUsersQuery();

  const filteredUsers = useMemo(() => {
    return fuzzysort(
      searchQuery,
      allUsers.filter(
        (el) =>
          el.oktaId !== currentUser?.oktaId &&
          !selectedUsers.find((e) => el.oktaId === e.oktaId),
      ),
      {
        keys: ["firstName", "email"],
        all: true,
        limit: 5,
      },
    ).map((e) => e.obj);
  }, [searchQuery, allUsers, selectedUsers, currentUser]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      e.currentTarget.blur();
      setSelectedSearch(-1);
      setIsOpen(false);
      return;
    }

    const isArrowDown = e.key === "ArrowDown";
    const isArrowUp = e.key === "ArrowUp";
    if (isArrowUp || isArrowDown) {
      e.preventDefault();
      setSelectedSearch((prev) => {
        const newVal = prev + (isArrowDown ? 1 : -1);

        if (newVal > filteredUsers.length - 1) {
          return 0;
        }
        if (newVal < 0) {
          return filteredUsers.length - 1;
        }

        return newVal;
      });
      return;
    }

    if (e.key === "Enter") {
      e.currentTarget.blur();
      if (filteredUsers.length === 0) return;

      if (selectedSearch < 0) {
        onSelect(filteredUsers[0] as IUser);
        setSelectedSearch(-1);
        setIsOpen(false);
      }

      const user = filteredUsers[selectedSearch];
      if (user) {
        onSelect(user as IUser);
        setSelectedSearch(-1);
        setIsOpen(false);
      }

      return;
    }
  };

  return (
    <div className="tw-relative" ref={containerRef}>
      <IconUsersPlus
        className={cn(
          "tw-absolute tw-left-3 tw-top-1/2 tw-z-10 tw-size-4 -tw-translate-y-1/2",
          !isShareEveryone && (searchQuery.length > 0 || isOpen)
            ? "tw-text-primary"
            : "tw-text-blue-500",
        )}
      />
      <Input
        placeholder="Add people to your folder"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="tw-h-9 tw-rounded-lg tw-px-3 tw-pl-9 tw-font-sans tw-text-base tw-font-normal"
        disabled={isLoadingUsers}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => setIsOpen(true)}
      />

      {isOpen && filteredUsers.length > 0 && (
        <div
          className="tw-absolute tw-left-0 tw-top-full tw-z-10 tw-mt-2 tw-w-full tw-rounded-lg tw-border tw-border-blue-100 tw-bg-white tw-p-2 tw-shadow-md">
          <ol className="tw-m-0 tw-grid tw-list-none tw-content-start tw-gap-1">
            {filteredUsers.map((user, idx) => (
              <User
                key={user._id}
                user={user as IUser}
                isSelected={selectedSearch === idx}
                onSelect={() => {
                  onSelect(user as IUser);
                  setSelectedSearch(-1);
                  setIsOpen(false);
                }}
              />
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

interface IUserProps {
  user: IUser;
  isSelected: boolean;
  onSelect: () => void;
}

const User = ({ user, isSelected, onSelect }: IUserProps) => {
  const initials = user.firstName
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");
  const bgColor = user.profile?.color ?? "#D0CDD8";
  const textColor = user.profile?.theme === "dark" ? "white" : "#413565";
  const picture = user.profile?.picture;
  return (
    <li
      onClick={onSelect}
      className={cn(
        "tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded tw-p-2 hover:tw-bg-blue-50",
        isSelected && "tw-bg-blue-50",
      )}
    >
      <div
        className="tw-grid tw-size-8 tw-place-items-center tw-overflow-hidden tw-rounded-full"
        style={{
          background: bgColor,
        }}
      >
        {picture ? (
          <img
            src={picture}
            alt={user?.firstName}
            className="tw-block tw-h-full tw-w-full tw-object-cover"
          />
        ) : (
          <span
            className="tw-font-sans tw-text-xs/tight tw-font-semibold tw-uppercase"
            style={{
              color: textColor,
            }}
          >
            {initials}
          </span>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-justify-center">
        <div className="tw-text-[11px] tw-leading-normal tw-text-blue-800">
          {user.firstName}
        </div>
        <div className="tw-text-[9px] tw-leading-normal tw-text-blue-500">
          {user.email}
        </div>
      </div>
    </li>
  );
};
